if (!customElements.get('gpt-eu-description')) {
  class GPTCustomDescription extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      queueMicrotask(() => {
        if (!this.isConnected || signal.aborted) return;
        this.init(signal);
      });
    }
    disconnectedCallback() {
      this.tabResizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
    init(signal) {
      const tablist = this.querySelector('.gpt-cd-tabs');
      this.panels = [...this.querySelector('.gpt-cd-panels').children].filter(el => el.matches('[data-cd-panel]'));
      tablist.replaceChildren();
      this.tabs = this.panels.map((panel, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `${panel.id}-tab`;
        button.className = 'gpt-cd-tab';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        const icon = panel.querySelector('[data-cd-icon]');
        if (icon) {
          const wrapper = document.createElement('span');
          wrapper.setAttribute('aria-hidden', 'true');
          wrapper.append(icon.content.cloneNode(true));
          button.append(wrapper);
        }
        button.append(document.createTextNode(panel.dataset.title || 'Custom Description'));
        button.addEventListener('click', () => this.activate(index), { signal });
        button.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          this.activate(target);
          this.tabs[target].focus();
          this.tabs[target].scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }, { signal });
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        panel.tabIndex = 0;
        tablist.append(button);
        this.group(panel);
        return button;
      });
      this.sliders = [...this.querySelectorAll('[data-cd-slider]')].map(slider => this.initSlider(slider, signal));
      this.activate(Math.min(this.panels.length - 1, Math.max(0, Number(this.dataset.default || 1) - 1)));
      tablist.hidden = this.panels.length === 0;
      this.classList.add('is-enhanced');
      this.initTabHints(tablist, signal);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const panel = event.target.closest('[data-cd-panel]');
        if (panel) this.activate(this.panels.indexOf(panel));
        const slide = event.target.closest('[data-cd-kind="slide"]');
        const slider = this.sliders.find(item => item.slides.includes(slide));
        if (slider) slider.show(slider.slides.indexOf(slide));
      }, { signal });
    }
    initTabHints(tablist, signal) {
      const nav = this.querySelector('.gpt-cd-tab-nav');
      if (!nav) return;
      const back = nav.querySelector('[data-tab-back]');
      const forward = nav.querySelector('[data-tab-forward]');
      const hint = this.querySelector('.gpt-cd-tab-hint');
      const update = () => {
        const max = tablist.scrollWidth - tablist.clientWidth;
        const overflow = max > 2;
        const position = Math.abs(tablist.scrollLeft);
        nav.classList.toggle('has-overflow', overflow);
        nav.classList.toggle('can-go-back', overflow && position > 2);
        nav.classList.toggle('can-go-forward', overflow && position < max - 2);
        back.disabled = !overflow || position <= 2;
        forward.disabled = !overflow || position >= max - 2;
        hint.hidden = !overflow;
      };
      const scroll = direction => {
        const rtl = getComputedStyle(tablist).direction === 'rtl';
        tablist.scrollBy({ left: direction * (rtl ? -1 : 1) * tablist.clientWidth * 0.7,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      back.addEventListener('click', () => scroll(-1), { signal });
      forward.addEventListener('click', () => scroll(1), { signal });
      tablist.addEventListener('scroll', update, { signal, passive: true });
      window.addEventListener('resize', update, { signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.tabResizeObserver = new ResizeObserver(update);
        this.tabResizeObserver.observe(tablist);
        this.tabs.forEach(tab => this.tabResizeObserver.observe(tab));
      }
      update();
    }
    activate(index) {
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        this.tabs[i].setAttribute('aria-selected', String(i === index));
        this.tabs[i].tabIndex = i === index ? 0 : -1;
      });
    }
    group(panel) {
      const content = panel.querySelector('.gpt-cd-content');
      let group, track, previous;
      [...content.children].forEach(module => {
        const kind = module.dataset.cdKind;
        if (!['card', 'slide', 'care'].includes(kind)) {
          group = null;
          previous = null;
          return;
        }
        if (kind !== previous || !group) {
          group = document.createElement('div');
          group.className = kind === 'card' ? 'cozynest-spec-grid' : kind === 'care' ? 'dowinx-care-grid' : 'cozynest-slider';
          content.insertBefore(group, module);
          track = group;
          if (kind === 'slide') {
            group.dataset.cdSlider = '';
            track = document.createElement('div');
            track.className = 'cozynest-slides';
            group.append(track);
          }
        }
        track.append(module);
        previous = kind;
      });
    }
    initSlider(element, signal) {
      const slides = [...element.querySelector('.cozynest-slides').children];
      element.querySelectorAll('[data-cd-controls]').forEach(el => el.remove());
      element.setAttribute('role', 'region');
      element.setAttribute('aria-label', this.dataset.carousel);
      let current = 0;
      const dots = [];
      const status = document.createElement('span');
      status.className = 'gpt-cd-sr';
      status.dataset.cdControls = '';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const show = (index, announce = false) => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.hidden = i !== current;
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-label', `${this.dataset.slide} ${i + 1} / ${slides.length}`);
          if (dots[i]) dots[i].setAttribute('aria-current', String(i === current));
        });
        if (announce) status.textContent = `${this.dataset.slide} ${current + 1} / ${slides.length}`;
      };
      if (slides.length > 1) {
        const controls = document.createElement('div');
        controls.dataset.cdControls = '';
        ['previous', 'next'].forEach((direction, i) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `cozynest-arrow ${i ? 'next' : 'prev'}`;
          button.textContent = i ? '❯' : '❮';
          button.setAttribute('aria-label', this.dataset[direction]);
          button.addEventListener('click', () => show(current + (i ? 1 : -1), true), { signal });
          controls.append(button);
        });
        const nav = document.createElement('div');
        nav.className = 'cozynest-dots';
        slides.forEach((slide, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cozynest-dot';
          dot.setAttribute('aria-label', `${this.dataset.slide} ${i + 1}`);
          dot.addEventListener('click', () => show(i, true), { signal });
          dots.push(dot);
          nav.append(dot);
        });
        controls.append(nav, status);
        element.append(controls);
        let startX;
        element.addEventListener('touchstart', event => { startX = event.touches[0].clientX; }, { signal, passive: true });
        element.addEventListener('touchend', event => {
          if (startX === undefined) return;
          const distance = event.changedTouches[0].clientX - startX;
          if (Math.abs(distance) > 50) show(current + (distance < 0 ? 1 : -1), true);
          startX = undefined;
        }, { signal, passive: true });
      }
      show(0);
      return { slides, show };
    }
  }
  customElements.define('gpt-eu-description', GPTCustomDescription);
}
